import { Box, Card, Button, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetAtom, useAtomValue } from 'jotai';
import { Business_Profile_Enum } from '@kheops/graphql';
import { ContractsByCompanyIdQuery } from '../queries/__generated__/contractsByCompanyId.generated';
import { RoutePaths } from '../routes/AppRoutes';
import TopAppBar from '../common/components/TopAppBar';
import { currentContextAtom } from '../state';
import ContactableCompanyRow from './ContactableCompanyRow';
import { newChannelSlideOpenAtom } from './state';

type ContactableCompanies = ContractsByCompanyIdQuery['contract'];

interface ContactableCompanyListProps {
  contracts: ContactableCompanies;
  availableChannels: GroupChannel[];
}

export default function ContactableCompanyList({ contracts, availableChannels }: ContactableCompanyListProps): React.JSX.Element {
  const { t } = useTranslation(['chat']);
  const { realm } = useAtomValue(currentContextAtom);
  const setNewChannelSlideOpen = useSetAtom(newChannelSlideOpenAtom);

  const sortedContracts = useMemo(() => {
    if (!contracts.length) {
      return [];
    }

    return contracts
      .filter(({ channel_url }) => !availableChannels.some(({ url }) => channel_url === url))
      .sort((a, b) => {
        const companyKey = realm === Business_Profile_Enum.Buyer ? 'supplying_company' : 'buying_company';

        return a[companyKey].tradeName.toLowerCase().localeCompare(b[companyKey].tradeName.toLowerCase());
      });
  }, [contracts, availableChannels]);

  return (
    <>
      <TopAppBar
        configuration="small"
        title={t('chat:new_discussion')}
        backTo={RoutePaths.ROOT}
        startAction={(
          <Button variant="text" onClick={() => setNewChannelSlideOpen(false)}>
            <ArrowBackIcon />
          </Button>
        )}
        sx={{
          position: {
            xs: 'fixed',
            lg: 'none',
          },
        }}
      />
      <Card
        className="Kheops-ChatListPage-container"
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          position: 'absolute',
          top: 0,
          left: 0,
          width: {
            xs: `calc(100% - ${theme.spacing(2)})`,
            md: `calc(100% - ${theme.spacing(2)})`,
          },
          height: {
            xs: `calc(100% - ${theme.spacing(4)})`,
            md: `calc(100% - ${theme.spacing(4)})`,
          },
          zIndex: 1,
          gap: 2,
          px: 1,
          py: 2,
        })}
      >
        <Box
          sx={{
            display: {
              xs: 'none',
              lg: 'flex',
            },
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Button variant="text" onClick={() => setNewChannelSlideOpen(false)}>
            <ArrowBackIcon />
          </Button>
          <Typography variant="titleLarge">{t('chat:new_discussion')}</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
          {sortedContracts.map((contract) => (
            <ContactableCompanyRow contract={contract} key={contract.id} />
          ))}
        </Box>
      </Card>
    </>
  );
}
